import React, { useState } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getYouTubeVideoId, getVimeoVideoId } from "./helpers/utils.js";
import IframeWithSpinner from "./helpers/IframeWithSpinner.js";

// DJ RISE waveform: the same 42 bars and height formula as the mockup.
const WAVE = Array.from({ length: 42 }, (_, i) =>
  12 + Math.abs(Math.sin(i * 0.55) * 30 + Math.sin(i * 1.7) * 12)
);

const Dashboard = () => (
  <div className="shot">
    <div className="side">
      <div />
      <div />
      <div />
      <div />
      <div />
    </div>
    <div className="main">
      <div className="w" />
      <div />
      <div />
      <div />
    </div>
  </div>
);

const Wave = () => (
  <div className="wave">
    {WAVE.map((h, i) => (
      <i key={i} style={{ height: `${h}px` }} />
    ))}
  </div>
);

const Spec = () => (
  <div
    className="mono"
    style={{
      marginTop: 16,
      fontSize: 12,
      lineHeight: 1.9,
      background: "#0d0f12",
      border: "1px solid var(--line)",
      borderRadius: 10,
      padding: "12px 14px",
      color: "#aab1bd",
    }}
  >
    <span style={{ color: "var(--acc)" }}>api</span>: OpenAPI 3.1 · contract first
    <br />
    <span style={{ color: "var(--acc2)" }}>client</span>: generated TypeScript
    <br />
    <span style={{ color: "var(--acc)" }}>infra</span>: AWS CDK · ECS Fargate · RDS
    <br />
    <span style={{ color: "#f2b56b" }}>alarms</span>: 38 CloudWatch
  </div>
);

const VISUALS = { dashboard: Dashboard, wave: Wave, spec: Spec };

const ProjectCard = ({
  className,
  kicker,
  title,
  description,
  visual,
  chips,
  link,
  linkLabel,
  detailContent,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const isDialogProject = link === "dialog";
  const youtubeId = !isDialogProject ? getYouTubeVideoId(link) : null;
  const vimeoId = !isDialogProject ? getVimeoVideoId(link) : null;
  const Visual = VISUALS[visual];

  const video = youtubeId ? (
    <IframeWithSpinner
      src={`https://www.youtube.com/embed/${youtubeId}`}
      title={title}
    />
  ) : vimeoId ? (
    <IframeWithSpinner
      src={`https://player.vimeo.com/video/${vimeoId}?h=0eb484de83`}
      title={title}
    />
  ) : null;

  const heading = (
    <>
      <span className="kick mono">{kicker}</span>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
    </>
  );

  // Video tiles: the Agents Dashboard row keeps its text in its own column.
  if (video) {
    return (
      <div className={`tile ${className}`}>
        {className === "t-ag" ? <div>{heading}</div> : heading}
        <div className="video embed">{video}</div>
      </div>
    );
  }

  return (
    <div className={`tile ${className}`}>
      {heading}
      {Visual ? <Visual /> : null}
      {chips?.length ? (
        <div className="chips">
          {chips.map((c) => (
            <span className="chip" key={c}>
              {c}
            </span>
          ))}
        </div>
      ) : null}
      {isDialogProject ? (
        <button type="button" className="more" onClick={() => setDialogOpen(true)}>
          View details
        </button>
      ) : link ? (
        <a className="more" href={link} target="_blank" rel="noopener noreferrer">
          {linkLabel ?? link}
        </a>
      ) : null}

      {isDialogProject && detailContent && (
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "85vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>{detailContent}</DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default ProjectCard;
